import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Search, Clock, MapPin, Wrench, Eye, CalendarDays,
  AlertTriangle, CheckCircle, RefreshCw, X
} from 'lucide-react';

const STATUS_CONFIG = {
  open: { label: 'Nou', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  accepted: { label: 'Acceptat', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  in_progress: { label: 'In Lucru', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  waiting_parts: { label: 'Asteapta Piese', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  resolved: { label: 'Rezolvat', color: 'bg-green-100 text-green-700 border-green-200' },
  closed: { label: 'Inchis', color: 'bg-slate-100 text-slate-600 border-slate-200' }
};

const PRIORITY_CONFIG = {
  critical: { label: 'CRITIC', color: 'bg-red-500 text-white' },
  urgent: { label: 'URGENT', color: 'bg-orange-500 text-white' },
  high: { label: 'RIDICAT', color: 'bg-amber-500 text-white' },
  medium: { label: 'MEDIU', color: 'bg-yellow-500 text-slate-900' },
  low: { label: 'SCAZUT', color: 'bg-green-500 text-white' }
};

const ACTIVE_STATUSES = ['open', 'accepted', 'in_progress', 'waiting_parts'];

const PortalTicketsPage = () => {
  const { user } = useAuth();
  const { selectedPlant, selectedLocation } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-driven equipment filter (when navigated from Reports → Top Equipment)
  const equipmentIdFilter = searchParams.get('equipment_id') || '';
  const [equipmentName, setEquipmentName] = useState('');

  // Fetch equipment name (once) when equipment_id is in URL
  useEffect(() => {
    let cancelled = false;
    if (equipmentIdFilter) {
      api.get(`/equipment/${equipmentIdFilter}`)
        .then(res => { if (!cancelled) setEquipmentName(res.data?.name || ''); })
        .catch(() => { if (!cancelled) setEquipmentName(''); });
    } else {
      setEquipmentName('');
    }
    return () => { cancelled = true; };
  }, [equipmentIdFilter]);

  const clearEquipmentFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('equipment_id');
    setSearchParams(next, { replace: true });
  };
  
  // Server-side paginated data
  const [pageData, setPageData] = useState({
    items: [],
    total: 0,
    page: 1,
    pages: 1,
    page_size: 50,
    tab_counts: { active: 0, closed: 0, all: 0 },
    period_counts: { today: 0, yesterday: 0, this_week: 0, this_month: 0, last_month: 0, all: 0 }
  });
  
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(equipmentIdFilter ? 'all' : 'active');
  const [timePeriod, setTimePeriod] = useState('all');
  const searchDebounceRef = useRef(null);

  // ── Core fetch (server-side paginated) ────────────────────────────────────
  const fetchPage = useCallback(async (opts = {}) => {
    const {
      pg = page,
      tab = activeTab,
      period = timePeriod,
      search = searchTerm,
      silent = false,
    } = opts;
    
    if (!silent) setLoading(true);
    
    try {
      const params = new URLSearchParams({
        page: pg,
        page_size: PAGE_SIZE,
        status_group: tab,
        time_period: period,
      });
      
      // Add user's plant/location access for filtering
      if (user?.plant_access?.length > 0) {
        params.append('plant_access', user.plant_access.join(','));
      }
      if (user?.location_access?.length > 0) {
        params.append('location_access', user.location_access.join(','));
      }
      
      // Add selected plant/location from layout filters
      if (selectedPlant && selectedPlant !== 'all') {
        params.append('plant_id', selectedPlant);
      }
      if (selectedLocation && selectedLocation !== 'all') {
        params.append('location_id', selectedLocation);
      }

      // Equipment filter (when navigated from Reports)
      if (equipmentIdFilter) {
        params.append('equipment_id', equipmentIdFilter);
      }

      if (search && search.length >= 2) {
        params.append('search', search);
      }
      
      const res = await api.get(`/tickets/page?${params}`);
      setPageData(res.data);
    } catch {
      toast.error('Eroare la incarcarea tichetelor');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, activeTab, timePeriod, searchTerm, user, selectedPlant, selectedLocation, equipmentIdFilter]);

  // Initial load & refetch on filter change
  useEffect(() => {
    fetchPage({ pg: page, tab: activeTab, period: timePeriod, search: searchTerm });
  }, [page, activeTab, timePeriod, selectedPlant, selectedLocation, equipmentIdFilter]); // eslint-disable-line

  // Debounced search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage({ pg: 1, tab: activeTab, period: timePeriod, search: value });
    }, 350);
  };

  // Filter change helpers
  const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); setTimePeriod('all'); };
  const handlePeriodChange = (p) => { setTimePeriod(p); setPage(1); };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatRelative = (d) => {
    if (!d) return '';
    const diff = (Date.now() - new Date(d).getTime()) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}z`;
  };

  const tickets = pageData.items;
  const tabCounts = pageData.tab_counts;
  const periodCounts = pageData.period_counts;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-20 lg:pb-6" data-testid="portal-tickets-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="portal-tickets-title">Tichete Sectie</h1>
          <p className="text-sm text-slate-500">
            {pageData.total} tichete total
            {pageData.pages > 1 && ` • Pagina ${pageData.page} din ${pageData.pages}`}
          </p>
        </div>
        <Button onClick={() => fetchPage()} variant="outline" size="sm" data-testid="refresh-tickets-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Reimprospateaza
        </Button>
      </div>

      {/* Active equipment filter banner (when navigated from Reports) */}
      {equipmentIdFilter && (
        <div
          className="flex items-center gap-3 px-4 py-3 mb-4 rounded-xl border border-blue-200 bg-blue-50"
          data-testid="equipment-filter-banner"
        >
          <Wrench className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-blue-900">
              Filtrat dupa echipament: <span className="font-semibold">{equipmentName || equipmentIdFilter.slice(0, 8).toUpperCase()}</span>
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearEquipmentFilter}
            className="text-blue-700 hover:bg-blue-100 h-8 px-3"
            data-testid="clear-equipment-filter-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Elimina filtru
          </Button>
        </div>
      )}

      {/* Status Tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="status-tabs">
        {[
          { key: 'active', label: 'Active', icon: AlertTriangle, accentColor: 'text-red-600' },
          { key: 'closed', label: 'Finalizate', icon: CheckCircle, accentColor: 'text-green-600' },
          { key: 'all', label: 'Toate', icon: null, accentColor: 'text-slate-600' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
              activeTab === tab.key
                ? 'bg-white text-slate-900 border-slate-300 shadow-sm'
                : 'bg-transparent text-slate-500 border-transparent hover:bg-white/60 hover:text-slate-700'
            }`}
          >
            {tab.icon && <tab.icon className={`w-4 h-4 ${activeTab === tab.key ? tab.accentColor : ''}`} />}
            {tab.label}
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              activeTab === tab.key ? 'bg-slate-100 text-slate-700' : 'bg-slate-100/50 text-slate-400'
            }`}>{tabCounts[tab.key]}</span>
          </button>
        ))}
      </div>

      {/* Time Period Filter */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 mb-4" data-testid="time-period-filter">
        <CalendarDays className="w-4 h-4 text-slate-400 flex-shrink-0" />
        {[
          { key: 'today', label: 'Azi' },
          { key: 'yesterday', label: 'Ieri' },
          { key: 'this_week', label: 'Saptamana' },
          { key: 'this_month', label: 'Luna' },
          { key: 'last_month', label: 'Luna trecuta' },
          { key: 'all', label: 'Toate' },
        ].map(p => (
          <button
            key={p.key}
            onClick={() => handlePeriodChange(p.key)}
            data-testid={`time-filter-${p.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
              timePeriod === p.key
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
            }`}
          >
            {p.label}
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
              timePeriod === p.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
            }`}>{periodCounts[p.key]}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Cauta tichet, echipament, locatie..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10 h-11"
          data-testid="search-tickets-input"
        />
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-slate-500">Se incarca...</p>
        </div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
            <h3 className="text-lg font-medium text-slate-900">Niciun tichet gasit</h3>
            <p className="text-slate-500 text-sm">Modifica filtrele sau perioada pentru a vedea mai multe rezultate</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="tickets-list">
          {tickets.map(ticket => {
            const priority = ticket.priority_assigned || ticket.priority_requested || 'medium';
            const pCfg = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
            const sCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
            const isOverdue = ACTIVE_STATUSES.includes(ticket.status) && (Date.now() - new Date(ticket.created_at).getTime()) > 48 * 3600000;

            return (
              <Card
                key={ticket.id}
                data-testid={`ticket-card-${ticket.id}`}
                className={`hover:shadow-md transition-all cursor-pointer ${isOverdue ? 'border-l-4 border-l-red-400' : ''}`}
                onClick={() => navigate(`/portal/tickets/${ticket.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Priority + Status row */}
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <Badge className={`text-[10px] px-2 py-0.5 ${pCfg.color}`}>{pCfg.label}</Badge>
                        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 ${sCfg.color}`}>{sCfg.label}</Badge>
                        {ticket.status === 'waiting_parts' && ticket.parts_eta_days && (
                          <Badge className="bg-orange-500 text-white text-[10px] px-2 py-0.5 font-bold" data-testid={`portal-list-eta-${ticket.id}`}>
                            ETA: {ticket.parts_eta_days}z{ticket.parts_eta_date ? ` · ${new Date(ticket.parts_eta_date).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' })}` : ''}
                          </Badge>
                        )}
                        {isOverdue && <Badge className="bg-red-100 text-red-700 text-[10px] px-2 py-0.5">INTARZIAT</Badge>}
                        <span className="text-[10px] text-slate-400 ml-auto">{formatRelative(ticket.created_at)}</span>
                      </div>
                      {/* Title */}
                      <h3 className="font-semibold text-slate-900 text-sm mb-1 truncate">{ticket.title}</h3>
                      {/* Info row */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {ticket.location_name || 'N/A'}
                        </span>
                        {ticket.equipment_name && (
                          <span className="flex items-center gap-1">
                            <Wrench className="w-3 h-3 text-slate-400" />
                            {ticket.equipment_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {formatDate(ticket.created_at)}
                        </span>
                      </div>
                      {ticket.assigned_to_name && (
                        <p className="text-xs text-slate-500 mt-1">
                          Asignat: <span className="font-medium text-slate-700">{ticket.assigned_to_name}</span>
                        </p>
                      )}
                    </div>
                    <Button variant="ghost" size="sm" className="shrink-0 text-slate-400 hover:text-blue-600" data-testid={`view-ticket-${ticket.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/portal/tickets/${ticket.id}`); }}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {pageData.pages > 1 && (
        <div className="flex items-center justify-between pt-4" data-testid="pagination-controls">
          <p className="text-sm text-slate-500">
            {pageData.total} tichete • Pagina {pageData.page} din {pageData.pages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="h-8 px-2 text-xs"
            >«</Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 px-3 text-xs"
            >Anterior</Button>
            <span className="px-3 text-sm font-medium text-slate-700">{page}</span>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(p => Math.min(pageData.pages, p + 1))}
              disabled={page >= pageData.pages}
              className="h-8 px-3 text-xs"
            >Urmator</Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage(pageData.pages)}
              disabled={page >= pageData.pages}
              className="h-8 px-2 text-xs"
            >»</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalTicketsPage;
